import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import TrashButton from "~/components/themed/TrashButton";
import { FormControl, FormField } from "~/components/v3/shadcn/Form";
import { Label } from "~/components/v3/shadcn/Label";
import { Input } from "~/components/v3/themed/Input";
import { RSVPStepsFormInputData } from "../validators";
import { getRSVPStepsAndPageData } from "../actions/rsvp-steps-and-data";
import { If } from "~/components/v3/Conditional";

const AddAdditionalGuestsOfUserModalContent = ({
    setIsNextDisabled,
    eventTicketPackage,
}: {
    setIsNextDisabled: (disabled: boolean) => void;
    eventTicketPackage?: Awaited<
        ReturnType<typeof getRSVPStepsAndPageData>
    >["eventInfo"]["eventTicketPackage"];
}) => {
    const form = useFormContext<RSVPStepsFormInputData>();
    const [totalPrice, setTotalPrice] = useState<number>(0);

    const guestsInput = useFieldArray({
        control: form.control,
        name: "guests",
    });

    useEffect(() => {
        if (!eventTicketPackage) return;
        const selectedTicketId = form.watch("ticket.selectedTierId");
        const selectedTicket = eventTicketPackage?.ticketTiers.find(
            (tt) => tt.id === selectedTicketId,
        );
        if (!selectedTicket) return;
        const dollarAndCents =
            (selectedTicket.priceDollars ?? 0) * 100 +
            (selectedTicket.priceCents ?? 0);
        setTotalPrice(dollarAndCents * (guestsInput.fields.length + 1));
    }, [eventTicketPackage, guestsInput.fields.length]);

    useEffect(() => {
        if (guestsInput.fields.length > 0) {
            const isValid = guestsInput.fields.every((guest) => {
                return (
                    !!guest.name &&
                    guest.name.trim() !== "" &&
                    guest.name.toLocaleLowerCase() !== "guest"
                );
            });
            setIsNextDisabled(!isValid);
        } else {
            setIsNextDisabled(false);
        }
    }, [guestsInput.fields]);

    return (
        <div className="flex max-h-[250px] min-h-[150px] flex-col items-start gap-4">
            <div className="flex w-full items-center gap-6">
                <Label>Who will come with you?</Label>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => guestsInput.append({ name: "" })}
                        disabled={guestsInput.fields.length >= 5}
                        className="h-7 w-7 rounded-lg bg-v3-N-500 font-[500] text-white/80 transition-all duration-100 hover:text-white enabled:hover:bg-v3-N-400 enabled:active:scale-125 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        +
                    </button>
                    <span className="w-8 text-center">
                        {guestsInput.fields.length}
                    </span>

                    <button
                        onClick={() =>
                            guestsInput.remove(guestsInput.fields.length - 1)
                        }
                        disabled={guestsInput.fields.length <= 0}
                        className=":hover:text-white h-7 w-7 rounded-lg bg-v3-N-500 font-[500] text-white/80 transition-all duration-100 enabled:hover:bg-v3-N-400 enabled:active:scale-125 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        -
                    </button>
                </div>
                <If
                    condition={
                        !!eventTicketPackage &&
                        eventTicketPackage.ticketTiers.length > 0 &&
                        totalPrice > 0
                    }
                >
                    <span>Total: ${totalPrice / 100}</span>
                </If>
            </div>
            <div className="flex max-h-[200px] w-full flex-col gap-2 overflow-y-auto pr-4">
                {guestsInput.fields.map((guest, index) => (
                    <div key={index} className="flex w-full items-center gap-2">
                        <FormField
                            control={form.control}
                            name={`guests.${index}.name`}
                            render={({ field }) => (
                                <FormControl>
                                    <Input
                                        {...field}
                                        className="w-full"
                                        placeholder="Full guest name"
                                        onChange={(e) => {
                                            field.onChange(e);
                                            const currentGuests =
                                                form.getValues("guests");
                                            const isValid = currentGuests.every(
                                                (guest) =>
                                                    !!guest?.name &&
                                                    guest.name.trim() !== "" &&
                                                    guest.name.toLowerCase() !==
                                                        "guest",
                                            );
                                            setIsNextDisabled(!isValid);
                                        }}
                                    />
                                </FormControl>
                            )}
                        />
                        <TrashButton
                            onClick={() => guestsInput.remove(index)}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AddAdditionalGuestsOfUserModalContent;
